'use client';

import { useFormatter, useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import type {
  PriceAlertDeliveryStatus,
  PriceAlertFailureReason,
  PriceAlertTriggerDto,
} from '@/lib/api/priceAlerts';
import { useTelegramConnect } from '@/lib/hooks/useTelegramConnect';
import { Link } from '@/lib/routing';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/lib/ui-kit/tooltip';
import { cn } from '@/lib/ui-kit/utils';
import { Z_OVERLAY_MAX } from './constants';

interface PriceAlertHistoryCardProps {
  trigger: PriceAlertTriggerDto;
}

const STATUS_VALUE_COLOR: Record<PriceAlertDeliveryStatus, string> = {
  DELIVERED: 'text-v2-green',
  FAILED: 'text-red-400',
  PENDING: 'text-v2-text-primary',
};

const SETTINGS_HREF = '/account?tab=settings';

export function PriceAlertHistoryCard({ trigger }: PriceAlertHistoryCardProps) {
  const t = useTranslations('priceAlerts');
  const tHist = useTranslations('priceAlerts.history');
  const tFail = useTranslations('priceAlerts.history.failureReason');
  const format = useFormatter();
  const { connect: connectTelegram } = useTelegramConnect();

  const isYes = trigger.side === 'YES';
  const triggeredAt = new Date(trigger.triggeredAt);
  const dateText = format.dateTime(triggeredAt, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  });

  const statusLabels: Record<PriceAlertDeliveryStatus, string> = {
    DELIVERED: tHist('delivered'),
    FAILED: tHist('failed'),
    PENDING: tHist('pending'),
  };

  const failureContent =
    trigger.deliveryStatus === 'FAILED' && trigger.failureReason
      ? renderFailureReason(tFail, trigger.failureReason, connectTelegram)
      : null;

  const channelLabel = t(`channels.${trigger.deliveryChannel}`);
  const statusValueClass = STATUS_VALUE_COLOR[trigger.deliveryStatus];

  const statusInline = (
    <span className={cn('text-[12px] leading-[150%] font-normal', statusValueClass)}>
      {statusLabels[trigger.deliveryStatus]}
      {channelLabel && <> {tHist('inChannel', { channel: channelLabel })}</>}
    </span>
  );

  return (
    <div className="flex flex-col gap-4 rounded-[10px] bg-v2-btn-secondary p-4">
      {/* Header: trigger title + side badge + event link */}
      <div className="flex flex-col gap-2">
        <span className="text-[12px] leading-4 font-normal text-v2-text-primary">
          {trigger.displayText}
        </span>
        <div className="flex min-w-0 items-center gap-1">
          <span
            className={cn(
              'shrink-0 rounded-[8px] px-2 py-0.5 text-[12px] leading-4 font-normal',
              isYes ? 'bg-v2-green-surface text-v2-green' : 'bg-v2-red-surface text-red-400',
            )}
          >
            {isYes ? t('side.yes') : t('side.no')}
          </span>
          {trigger.eventSlug ? (
            <Link
              href={`/event/${trigger.eventSlug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="truncate text-[13px] leading-4 font-normal text-v2-text-secondary transition-colors hover:text-v2-text-primary hover:underline"
            >
              {trigger.eventName}
            </Link>
          ) : (
            <span className="truncate text-[13px] leading-4 font-normal text-v2-text-secondary">
              {trigger.eventName}
            </span>
          )}
        </div>
      </div>

      {/* Time | Status */}
      <div className="grid grid-cols-2 gap-4">
        <CardField label={tHist('time')}>{dateText}</CardField>
        <CardField label={tHist('status')}>
          {failureContent ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0} className="cursor-pointer">
                  {statusInline}
                </span>
              </TooltipTrigger>
              <TooltipContent
                side="top"
                align="end"
                showArrow={false}
                style={{ zIndex: Z_OVERLAY_MAX }}
                className="w-fit max-w-[280px] rounded-[10px] border-0 bg-[#222628] p-4 text-xs leading-4 font-normal tracking-[-0.01em] text-pretty text-white"
              >
                {failureContent}
              </TooltipContent>
            </Tooltip>
          ) : (
            statusInline
          )}
        </CardField>
      </div>
    </div>
  );
}

interface CardFieldProps {
  label: string;
  children: ReactNode;
}

function CardField({ label, children }: CardFieldProps) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="text-[10px] leading-none font-normal text-v2-text-secondary">{label}</span>
      <span className="text-[12px] leading-[150%] font-normal text-v2-text-primary">
        {children}
      </span>
    </div>
  );
}

function renderFailureReason(
  t: ReturnType<typeof useTranslations<'priceAlerts.history.failureReason'>>,
  reason: PriceAlertFailureReason,
  onConnectTelegram: () => void,
): ReactNode {
  const link = (chunks: ReactNode) => (
    <Link
      href={SETTINGS_HREF}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-300 underline underline-offset-2"
    >
      {chunks}
    </Link>
  );
  const bot = (chunks: ReactNode) => (
    <button
      type="button"
      onClick={onConnectTelegram}
      className="cursor-pointer text-blue-300 underline underline-offset-2"
    >
      {chunks}
    </button>
  );

  switch (reason) {
    case 'DISABLED':
      return t.rich('disabled', { link });
    case 'NO_CONTACT':
      return t.rich('noContact', { link });
    case 'TG_BOT_BLOCKED':
      return t.rich('tgBotBlocked', { bot });
    case 'TG_CHAT_NOT_FOUND':
      return t.rich('tgChatNotFound', { bot });
    case 'TG_UNAVAILABLE':
      return t('tgUnavailable');
    case 'INTERNAL_ERROR':
      return t('internalError');
    case 'INVALID_TARGET':
      return t.rich('invalidTarget', { link });
  }
}
